import React, { useState, useEffect } from 'react';
import {
  Paper,
  Button,
  FormHelperText,
  Box,
  Typography,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
} from '@mui/material';
import { CNPJField, DialogoMensagem } from '../../components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { pt } from 'yup-locale-pt';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useStoreActions } from 'easy-peasy';
import { useScript } from '../../hooks/useScript';
import api from '../../services/api';

yup.setLocale(pt);

const schema = yup
  .object({
    cnpj: yup.string().label('CNPJ').cnpj().required(),
  })
  .required();

const containerId = 'recaptcha_id';
const recaptchaSize = 'normal';

const isDevelopOrLocal = Boolean(
  typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.includes('develop') ||
      window.location.hostname.includes('dev-') ||
      window.location.hostname.startsWith('dev.') ||
      window.location.hostname.includes('homolog') ||
      process.env.NODE_ENV === 'development' ||
      process.env.REACT_APP_ENV === 'develop' ||
      (process.env.REACT_APP_API_URL_BASE &&
        (process.env.REACT_APP_API_URL_BASE.includes('dev') ||
          process.env.REACT_APP_API_URL_BASE.includes('develop') ||
          process.env.REACT_APP_API_URL_BASE.includes('localhost'))))
);

const Interesse = () => {
  const setCnpjValidado = useStoreActions(
    (actions) => actions.interesse.setCnpjValidado
  );
  const startLoading = useStoreActions((actions) => actions.nav.startLoading);
  const stopLoading = useStoreActions((actions) => actions.nav.stopLoading);
  const navigate = useNavigate();

  const [captchaResponse, setCaptchaResponse] = useState(null);
  const [captchaErrorMsg, setCaptchaErrorMsg] = useState(null);

  const [dialogReaberturaOpen, setDialogReaberturaOpen] = useState(false);
  const [pendingCnpj, setPendingCnpj] = useState('');
  const [razaoSocialExistente, setRazaoSocialExistente] = useState('');
  const [emailReabertura, setEmailReabertura] = useState('');
  const [telefoneReabertura, setTelefoneReabertura] = useState('');
  const [emailReaberturaError, setEmailReaberturaError] = useState(null);
  const [telefoneReaberturaError, setTelefoneReaberturaError] = useState(null);
  const [openMensagemSucesso, setOpenMensagemSucesso] = useState(false);

  const useFormRef = useForm({
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });
  const { handleSubmit } = useFormRef;

  const sitekey = process.env.REACT_APP_CAPTCHA_KEY || '';

  useRecaptcha({
    containerId,
    successCallback: (response) => {
      setCaptchaResponse(response);
      setCaptchaErrorMsg(null);
    },
    expiredCallback: () => setCaptchaResponse(null),
    sitekey,
    size: recaptchaSize,
    enabled: !isDevelopOrLocal,
  });

  const onSubmit = async (data) => {
    if (!isDevelopOrLocal && !captchaResponse) {
      setCaptchaErrorMsg('Captcha inválido');
      return;
    }
    const digits = data.cnpj.replace(/\D/g, '');
    startLoading();
    try {
      const { data: res } = await api.http.get(`/processos/exists/cnpj/${digits}`);
      if (!res.exists) {
        // O campo só confere os dígitos verificadores, então número inventado que
        // fecha a conta entrava no sistema. Aqui a gente pergunta pra Receita se a
        // empresa existe mesmo - e só barra quando ela responde que não existe: se a
        // consulta falhar ou bater no limite de requisições, a inscrição segue, pra
        // instabilidade de um serviço de terceiro não travar o cadastro.
        const receita = await api.http
          .get(`/consulta-cnpj/${digits}`)
          .then((r) => r.data)
          .catch(() => null);

        if (receita?.brasilApi?.status === 'not_found') {
          stopLoading({
            message: 'CNPJ não encontrado na Receita Federal. Confira o número digitado.',
            severity: 'error',
          });
          return;
        }

        stopLoading();
        setCnpjValidado(data.cnpj);
        navigate('/novo_processo');
        return;
      }
      stopLoading();
      setPendingCnpj(data.cnpj);
      setRazaoSocialExistente(res.razaoSocial || '');
      setEmailReabertura(res.email || '');
      setTelefoneReabertura(res.telefone != null ? String(res.telefone) : '');
      setEmailReaberturaError(null);
      setTelefoneReaberturaError(null);
      setDialogReaberturaOpen(true);
    } catch (error) {
      stopLoading({ message: error.toString(), severity: 'error' });
    }
  };

  const fecharDialogReabertura = () => {
    setDialogReaberturaOpen(false);
    setPendingCnpj('');
    setRazaoSocialExistente('');
    setEmailReabertura('');
    setTelefoneReabertura('');
    setEmailReaberturaError(null);
    setTelefoneReaberturaError(null);
  };

  const onConfirmarReabertura = async () => {
    const trimmed = (emailReabertura || '').trim();
    if (!trimmed) {
      setEmailReaberturaError('Informe um e-mail para contato.');
      return;
    }
    const emailSchema = yup.string().email('E-mail inválido').required('Informe o e-mail');
    try {
      await emailSchema.validate(trimmed);
      setEmailReaberturaError(null);
    } catch (e) {
      setEmailReaberturaError(e.message);
      return;
    }

    const telDigits = (telefoneReabertura || '').replace(/\D/g, '');
    if (telDigits.length < 10 || telDigits.length > 11) {
      setTelefoneReaberturaError('Informe o telefone com DDD (10 ou 11 dígitos).');
      return;
    }
    setTelefoneReaberturaError(null);

    startLoading();
    try {
      await api.http.post('/processos/solicitar-reabertura', {
        cnpj: pendingCnpj,
        email: trimmed,
        telefone: telefoneReabertura.trim(),
      });
      stopLoading();
      fecharDialogReabertura();
      setCaptchaResponse(null);
      setOpenMensagemSucesso(true);
    } catch (error) {
      stopLoading({ message: error.toString(), severity: 'error' });
    }
  };

  const onOkMensagemSucesso = () => {
    setOpenMensagemSucesso(false);
  };

  return (
    <Container sx={{ mt: 3 }}>
      <Typography color="inherit" variant="h5">
        Preencha o formulário abaixo e participe!
      </Typography>

      <DialogoMensagem
        title="Solicitação enviada"
        open={openMensagemSucesso}
        onClose={onOkMensagemSucesso}
        onOk={onOkMensagemSucesso}
      >
        Sua solicitação foi registrada. Enviamos uma confirmação para o e-mail informado.
        Os administradores do programa também foram notificados e poderão entrar em contato pelo e-mail e pelo telefone informados.
      </DialogoMensagem>

      <Dialog open={dialogReaberturaOpen} onClose={fecharDialogReabertura} maxWidth="sm" fullWidth>
        <DialogTitle>CNPJ já cadastrado</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Já existe um processo cadastrado para este CNPJ
            {razaoSocialExistente ? (
              <>
                {' '}
                (<strong>{razaoSocialExistente}</strong>)
              </>
            ) : null}
            . Se desejar que o processo seja <strong>reaberto</strong> para continuar o cadastro,
            confirme abaixo. Será enviado um e-mail aos administradores com o CNPJ, a razão social, o
            e-mail e o telefone de contato informados (novos ou alterados). Você também receberá um e-mail
            de confirmação.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="E-mail para contato"
            type="email"
            fullWidth
            variant="outlined"
            value={emailReabertura}
            onChange={(e) => {
              setEmailReabertura(e.target.value);
              setEmailReaberturaError(null);
            }}
            error={Boolean(emailReaberturaError)}
            helperText={emailReaberturaError || undefined}
            sx={{ mb: 1 }}
          />
          <TextField
            margin="dense"
            label="Telefone para contato"
            type="tel"
            fullWidth
            variant="outlined"
            value={telefoneReabertura}
            onChange={(e) => {
              setTelefoneReabertura(e.target.value);
              setTelefoneReaberturaError(null);
            }}
            error={Boolean(telefoneReaberturaError)}
            helperText={telefoneReaberturaError || undefined}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={fecharDialogReabertura} color="inherit">
            Não
          </Button>
          <Button onClick={onConfirmarReabertura} variant="contained" color="primary">
            Sim, solicitar reabertura
          </Button>
        </DialogActions>
      </Dialog>

      <Paper
        variant="outlined"
        className="center-box"
        sx={{ px: 4, pt: 2, pb: 3, mb: 7 }}
      >
        <p>Qual o CNPJ da sua empresa?</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <CNPJField field="cnpj" fullWidth useFormRef={useFormRef} schema={schema} />
        </form>

        {!isDevelopOrLocal && (
          <Box sx={{ my: 3 }}>
            <div id={containerId} />
            {captchaErrorMsg && (
              <FormHelperText error>{captchaErrorMsg}</FormHelperText>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={handleSubmit(onSubmit)}
        >
          PRÓXIMO
        </Button>

        <br />
        <br />

        {/* textos institucionais aqui */}
      </Paper>
    </Container>
  );
};

function useRecaptcha({ containerId, successCallback, expiredCallback, sitekey, size, enabled = true }) {
  useScript(enabled ? 'https://www.google.com/recaptcha/api.js?render=explicit' : null);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    if (window.grecaptcha && sitekey) {
      window.grecaptcha.ready(() => {
        const container = document.getElementById(containerId);
        const alreadyRendered = container && container.children && container.children.length > 0;
        if (!alreadyRendered) {
          window.grecaptcha.render(containerId, {
            sitekey,
            callback: successCallback,
            'expired-callback': expiredCallback,
            size,
          });
        }
      });
    }
  }, [containerId, successCallback, expiredCallback, sitekey, size, enabled]);
}

export default Interesse;
